import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Check, Download, Share, SquarePlus, WifiOff } from "lucide-react";
import {
  getAppIdentity,
  IOS_INSTALL_EVENT,
  promptInstallApp,
  useCanInstallApp,
} from "./index";

const PRIMARY_BTN =
  "inline-flex min-h-[48px] w-full items-center justify-center rounded-2xl border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] px-6 text-base font-semibold text-[hsl(var(--ds-primary))] transition-[transform,background-color] duration-150 hover:bg-[hsl(var(--ds-primary)/0.08)] active:scale-[0.98] active:bg-[hsl(var(--ds-primary)/0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ds-ring))] motion-reduce:transition-none";

// ponytail: relies on navigator.onLine + online/offline events; a captive portal
// reports "online". Upgrade path: ping a lightweight endpoint before hiding.
function OfflineScreen() {
  const [offline, setOffline] = useState(() => !navigator.onLine);

  useEffect(() => {
    const goOnline = () => setOffline(false);
    const goOffline = () => setOffline(true);
    window.addEventListener("online", goOnline);
    window.addEventListener("offline", goOffline);
    return () => {
      window.removeEventListener("online", goOnline);
      window.removeEventListener("offline", goOffline);
    };
  }, []);

  if (!offline) return null;

  return createPortal(
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="pwa-offline-title"
      className="fixed inset-0 z-[13000] flex select-none flex-col items-center justify-center bg-[hsl(var(--ds-bg))] px-8 text-center animate-in fade-in duration-200 motion-reduce:animate-none"
      style={{
        paddingTop: "env(safe-area-inset-top)",
        paddingBottom: "env(safe-area-inset-bottom)",
      }}
    >
      <div className="flex h-20 w-20 items-center justify-center rounded-3xl bg-[hsl(var(--ds-primary)/0.1)] text-[hsl(var(--ds-primary))]">
        <WifiOff className="h-9 w-9" strokeWidth={1.75} aria-hidden="true" />
      </div>
      <h2
        id="pwa-offline-title"
        className="mt-6 text-xl font-semibold text-[hsl(var(--ds-fg))]"
      >
        You&apos;re offline
      </h2>
      <p className="mt-2 max-w-xs text-sm leading-relaxed text-[hsl(var(--ds-muted-fg))]">
        Connect to the internet to continue. Your screen will come back as soon
        as you&apos;re online.
      </p>
      <div className="mt-8 w-full max-w-xs">
        <button
          type="button"
          onClick={() => setOffline(!navigator.onLine)}
          className={PRIMARY_BTN}
        >
          Try again
        </button>
      </div>
    </div>,
    document.body
  );
}

function IosInstallSheet() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const show = () => setOpen(true);
    window.addEventListener(IOS_INSTALL_EVENT, show);
    return () => window.removeEventListener(IOS_INSTALL_EVENT, show);
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  if (!open) return null;

  const { name, icons } = getAppIdentity();
  const steps = [
    { Icon: Share, text: <>Tap <b>Share</b> in the browser toolbar</> },
    { Icon: SquarePlus, text: <>Choose <b>Add to Home Screen</b></> },
    { Icon: Check, text: <>Tap <b>Add</b> to finish</> },
  ];

  return createPortal(
    <div className="fixed inset-0 z-[13000] flex items-end justify-center">
      <button
        type="button"
        aria-label="Close"
        onClick={() => setOpen(false)}
        className="absolute inset-0 cursor-default bg-black/40 animate-in fade-in duration-200 motion-reduce:animate-none"
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="pwa-ios-install-title"
        className="relative w-full max-w-md rounded-t-3xl bg-[hsl(var(--ds-surface))] px-6 pt-3 shadow-2xl animate-in slide-in-from-bottom duration-300 ease-out motion-reduce:animate-none"
        style={{ paddingBottom: "max(1.5rem, env(safe-area-inset-bottom))" }}
      >
        <div className="mx-auto mb-5 h-1.5 w-10 rounded-full bg-[hsl(var(--ds-border))]" />
        <div className="flex items-center gap-4">
          {icons[0] && (
            <img
              src={icons[0].src}
              alt=""
              className="h-14 w-14 shrink-0 rounded-2xl border border-[hsl(var(--ds-border))] shadow-sm"
            />
          )}
          <div className="min-w-0">
            <h2
              id="pwa-ios-install-title"
              className="text-lg font-semibold text-[hsl(var(--ds-fg))]"
            >
              Install app
            </h2>
            <p className="truncate text-sm text-[hsl(var(--ds-muted-fg))]">{name}</p>
          </div>
        </div>
        <ol className="mt-6 space-y-3">
          {steps.map(({ Icon, text }, i) => (
            <li
              key={i}
              className="flex items-center gap-4 rounded-2xl bg-[hsl(var(--ds-primary)/0.06)] px-4 py-3"
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] shadow-sm">
                <Icon className="h-5 w-5" aria-hidden="true" />
              </span>
              <span className="text-[15px] text-[hsl(var(--ds-fg))]">{text}</span>
            </li>
          ))}
        </ol>
        <div className="mt-6">
          <button type="button" onClick={() => setOpen(false)} className={PRIMARY_BTN}>
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

export function PwaOverlays() {
  return (
    <>
      <OfflineScreen />
      <IosInstallSheet />
    </>
  );
}

/** "Install app" user-menu entry; renders nothing when install isn't possible. */
export function InstallAppMenuItem({ className, onSelect }) {
  const canInstall = useCanInstallApp();
  if (!canInstall) return null;
  return (
    <button
      type="button"
      onClick={() => {
        onSelect?.();
        promptInstallApp();
      }}
      className={className}
    >
      <Download className="mr-2 h-4 w-4" />
      Install app
    </button>
  );
}
